import React, { useCallback, useMemo, useState } from 'react';
import { AlertTriangleIcon, Building2Icon, ClockIcon, FilterIcon, SearchIcon, ShieldCheckIcon } from 'lucide-react';
import { User, LicenseData, MichiganLicenseData, StateComplianceData, US_STATES, hasCompanyStateCompliance } from './types';
type CellStatus = 'not_started' | 'in_progress' | 'complete' | 'expired';
type StatusFilter = 'all' | 'complete' | 'in_progress' | 'expired';
interface LicenseMatrixProps {
  users: User[];
  licenses: Record<string, Record<string, LicenseData>>;
  michiganLicenses: Record<string, MichiganLicenseData>;
  stateCompliance: Record<string, StateComplianceData>;
  showCompanyRow: boolean;
  onCellClick: (userId: string, stateName: string) => void;
  onCompanyStateClick: (stateName: string) => void;
  onUserClick: (userId: string) => void;
}
const FILTER_OPTIONS: {
  key: StatusFilter;
  label: string;
  icon: string;
}[] = [{
  key: 'all',
  label: 'All',
  icon: ''
}, {
  key: 'complete',
  label: 'Complete',
  icon: '✅'
}, {
  key: 'in_progress',
  label: 'In Progress',
  icon: '🕐'
}, {
  key: 'expired',
  label: 'Expired',
  icon: '⚠️'
}];
const STATE_NAMES = US_STATES.map(s => s.name);
export function LicenseMatrix({
  users,
  licenses,
  michiganLicenses,
  stateCompliance,
  showCompanyRow,
  onCellClick,
  onCompanyStateClick,
  onUserClick
}: LicenseMatrixProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [licensedStatesOnly, setLicensedStatesOnly] = useState(false);
  // Pre-compute ALL statuses in one pass — single useMemo, no per-cell work
  const statusMap = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();
    const map: Record<string, Record<string, CellStatus>> = {};
    for (const user of users) {
      const userStatuses: Record<string, CellStatus> = {};
      for (const stateName of STATE_NAMES) {
        if (stateName === 'Michigan') {
          const mi = michiganLicenses[user.id];
          if (!mi) {
            userStatuses[stateName] = 'not_started';
          } else {
            const allComplete = mi.steps.every(Boolean);
            if (allComplete) {
              if (mi.expirationDate && new Date(mi.expirationDate).getTime() < todayTime && mi.renewals.length === 0) {
                userStatuses[stateName] = 'expired';
              } else {
                userStatuses[stateName] = 'complete';
              }
            } else if (mi.steps.some(Boolean)) {
              userStatuses[stateName] = 'in_progress';
            } else {
              userStatuses[stateName] = 'not_started';
            }
          }
        } else {
          const lic = licenses[user.id]?.[stateName];
          if (!lic) {
            userStatuses[stateName] = 'not_started';
          } else if (lic.status === 'complete' && lic.expirationDate && new Date(lic.expirationDate).getTime() < todayTime && lic.renewals.length === 0) {
            userStatuses[stateName] = 'expired';
          } else {
            userStatuses[stateName] = lic.status;
          }
        }
      }
      map[user.id] = userStatuses;
    }
    return map;
  }, [users, licenses, michiganLicenses]);
  // Filter users — uses pre-computed statusMap for O(1) lookups
  const filteredUsers = useMemo(() => {
    let result = users;
    const q = search.toLowerCase().trim();
    if (q) {
      result = result.filter(u => u.name.toLowerCase().includes(q) || u.nmlsId.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      result = result.filter(u => {
        const userStatuses = statusMap[u.id];
        if (!userStatuses) return false;
        return STATE_NAMES.some(s => userStatuses[s] === statusFilter);
      });
    }
    return result;
  }, [users, search, statusFilter, statusMap]);
  // Column filter — hide states with no licensing activity to cut visual noise
  const visibleStates = useMemo(() => {
    if (!licensedStatesOnly) return US_STATES;
    return US_STATES.filter(state => {
      if (showCompanyRow && hasCompanyStateCompliance(stateCompliance[state.name])) {
        return true;
      }
      return users.some(user => {
        const status = statusMap[user.id]?.[state.name];
        return status === 'complete' || status === 'expired';
      });
    });
  }, [licensedStatesOnly, showCompanyRow, stateCompliance, users, statusMap]);
  const handleCellClick = useCallback((userId: string, stateName: string) => {
    onCellClick(userId, stateName);
  }, [onCellClick]);
  const handleUserClick = useCallback((userId: string) => {
    onUserClick(userId);
  }, [onUserClick]);
  return <div className="flex-1 min-h-0 flex flex-col bg-slate-900/30 border border-slate-800/80 rounded-xl shadow-2xl overflow-hidden" data-id="element-181" data-mp="1pgprya75wytm">
      {/* Header Actions */}
      <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-700/50" data-id="element-182" data-mp="21s0a3fdmo63y">
        <div className="flex flex-col gap-0.5 flex-shrink-0" data-id="element-183" data-mp="26qtmf4h6hr2j">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-184" data-mp="2bqg17icnnfdg">
            Licensing & Compliance Grid
          </h2>
          <p className="text-[10px] text-slate-500" data-id="element-185" data-mp="2frjepy78czbi">Company requirements first · loan officers below</p>
        </div>

        <div className="flex items-center gap-1 bg-slate-800/40 border border-slate-700/40 rounded-lg p-0.5" data-id="element-186" data-mp="2kpkxei0quwmg">
          {FILTER_OPTIONS.map(opt => <button key={opt.key} onClick={() => setStatusFilter(opt.key)} className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${statusFilter === opt.key ? 'bg-slate-600/80 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-187" data-mp="2uhqlvhrsevww">
              {opt.icon && <span className="mr-1" data-id="element-188" data-mp="14dx7a9awwot9">{opt.icon}</span>}
              {opt.label}
            </button>)}
        </div>

        <button onClick={() => setLicensedStatesOnly(prev => !prev)} aria-pressed={licensedStatesOnly} className={`flex flex-shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${licensedStatesOnly ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300' : 'border-slate-700/50 bg-slate-800/50 text-slate-400 hover:text-slate-200'}`} data-id="element-189" data-mp="zby1ksrp9ft5">
          <FilterIcon className="h-3.5 w-3.5" data-id="element-190" data-mp="1w27a93mm01v5" />
          Licensed states only
          {licensedStatesOnly && <span className="ml-0.5 rounded bg-emerald-500/20 px-1 tabular-nums text-[10px] text-emerald-200" data-id="element-191" data-mp="1qafb0oykdvc">
              {visibleStates.length}
            </span>}
        </button>

        <div className="flex items-center bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-1.5 focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/30 w-56 flex-shrink-0" data-id="element-192" data-mp="3twfo52fp94uc">
          <SearchIcon className="w-3.5 h-3.5 text-slate-500 mr-2 flex-shrink-0" data-id="element-193" data-mp="3a0l0le047hmb" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search Officers..." className="w-full bg-transparent text-xs text-slate-200 focus:outline-none placeholder:text-slate-500" data-id="element-194" data-mp="39yibqxap7uj6" />
        </div>
      </div>

      {licensedStatesOnly && visibleStates.length === 0 && <div className="flex-shrink-0 border-b border-slate-700/50 bg-slate-900/60 px-4 py-2.5 text-xs text-slate-400" data-id="element-195" data-mp="2r3jxjyq4frl0">
          No licensed states yet. Turn off “Licensed states only” to see all states.
        </div>}

      {/* Matrix Grid */}
      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar bg-slate-900/50 relative" data-id="element-196" data-mp="u9gnj4ubank6">
        <table className="w-full border-collapse text-sm text-left" data-id="element-197" data-mp="22tw1yputi639">
          <thead className="sticky top-0 z-30 bg-slate-800 text-slate-400 text-xs uppercase tracking-wider" data-id="element-198" data-mp="1xujbzw8ngiwg">
            <tr data-id="element-199" data-mp="1t3kx913b7svb">
              <th className="sticky left-0 z-40 bg-slate-800 border-b border-r border-slate-700/50 px-4 py-3 min-w-[180px] max-w-[180px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-200" data-mp="1t31c7bid4od2">
                Officer
              </th>
              <th className="sticky left-[180px] z-40 bg-slate-800 border-b border-r border-slate-700/50 px-4 py-3 min-w-[100px] max-w-[100px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-201" data-mp="1k3xwmhiemr3b">
                NMLS ID
              </th>
              {visibleStates.map(state => <th key={state.abbr} className="border-b border-r border-slate-700/50 px-2 py-3 text-center min-w-[44px] max-w-[44px] font-semibold" title={state.name} data-id="element-202" data-mp="16458mty9ioz1">
                  {state.abbr}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50" data-id="element-203" data-mp="m9debk3h3ga3">
            {showCompanyRow && <tr className="bg-emerald-500/[0.07]" data-id="element-204" data-mp="r6vnnblejxim">
              <td className="sticky left-0 z-20 border-r border-emerald-500/20 bg-[#10231f] px-4 py-2.5 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-205" data-mp="vywwson9x84f">
                <div className="flex items-center gap-2 text-emerald-100" data-id="element-206" data-mp="1008nkxhvvze7">
                  <Building2Icon className="h-4 w-4 flex-shrink-0 text-emerald-400" data-id="element-207" data-mp="3c2p6sbk238d" />
                  <div className="min-w-0" data-id="element-208" data-mp="892lwcmiltmt">
                    <span className="block truncate text-sm font-bold" data-id="element-209" data-mp="8byhvvesy88m">Olympian Mortgage</span>
                    <span className="block text-[10px] font-medium uppercase tracking-wider text-emerald-400/80" data-id="element-210" data-mp="ca4nh03wmhry">Company compliance</span>
                  </div>
                </div>
              </td>
              <td className="sticky left-[180px] z-20 border-r border-emerald-500/20 bg-[#10231f] px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400/80 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-211" data-mp="1nsp6fa9g3vek">
                All states
              </td>
              {visibleStates.map(state => {
              const hasCompliance = hasCompanyStateCompliance(stateCompliance[state.name]);
              return <td key={state.abbr} className="border-r border-emerald-500/15 p-0 text-center align-middle" data-id="element-212" data-mp="13njbg0gpg10f">
                    <button onClick={() => onCompanyStateClick(state.name)} className="flex min-h-[52px] w-full items-center justify-center transition-colors hover:bg-emerald-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400" aria-label={`Open Olympian Mortgage compliance for ${state.name}`} title={`${state.name} company compliance`} data-id="element-213" data-mp="62nckvxepqht">
                      {hasCompliance ? <ShieldCheckIcon className="h-4 w-4 text-emerald-400" aria-hidden="true" data-id="element-214" data-mp="3fxt8hufarej1" /> : <span className="h-2 w-2 rounded-full border border-slate-600" aria-hidden="true" data-id="element-215" data-mp="3bxiqfje8cxnt" />}
                    </button>
                  </td>;
            })}
            </tr>}
            {filteredUsers.length === 0 ? <tr data-id="element-216" data-mp="2x7tlre6xibis">
                <td colSpan={visibleStates.length + 2} className="px-4 py-8 text-center text-slate-500" data-id="element-217" data-mp="2x8cuc4aovnk5">
                  No loan officers found.
                </td>
              </tr> : filteredUsers.map(user => {
            const userStatuses = statusMap[user.id];
            return <tr key={user.id} className="group hover:bg-slate-800/40" data-id="element-218" data-mp="meh1005on5ld">
                    <td className="sticky left-0 z-20 bg-slate-900 group-hover:bg-slate-800 border-r border-slate-700/50 px-4 py-2.5 font-medium text-slate-200 truncate shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-219" data-mp="hgp0ihqz2b5k">
                      <button onClick={() => handleUserClick(user.id)} className="hover:text-emerald-400 text-left w-full truncate" data-id="element-220" data-mp="15c0ogl74m5bw">
                        {user.name}
                      </button>
                    </td>
                    <td className="sticky left-[180px] z-20 bg-slate-900 group-hover:bg-slate-800 border-r border-slate-700/50 px-4 py-2.5 text-slate-400 tabular-nums shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-221" data-mp="1k8hwp3k0r3u8">
                      {user.nmlsId}
                    </td>
                    {visibleStates.map(state => {
                const status = userStatuses?.[state.name] || 'not_started';
                return <td key={state.abbr} onClick={() => handleCellClick(user.id, state.name)} className="border-r border-slate-700/50 p-0 cursor-pointer hover:bg-slate-700/50 text-center align-middle" data-id="element-222" data-mp="3uzhwdvk7njad">
                          <div className="flex items-center justify-center h-full w-full min-h-[40px]" data-id="element-223" data-mp="3nha52qr517cj">
                            {status === 'complete' && <span className="text-base leading-none" role="img" aria-label="Complete" data-id="element-224" data-mp="dx2gorz5inc">
                                ✅
                              </span>}
                            {status === 'in_progress' && <ClockIcon className="w-4 h-4 text-amber-400" strokeWidth={2.5} data-id="element-225" data-mp="3dri8jqzha45f" />}
                            {status === 'expired' && <AlertTriangleIcon className="w-4 h-4 text-red-400" strokeWidth={2.5} data-id="element-226" data-mp="2yq1y8rv86owo" />}
                          </div>
                        </td>;
              })}
                  </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}